<template>
  <div class="ae-history-panel" aria-label="Sessions">
    <div class="ae-card-head ae-card-head-stack">
      <div class="ae-title">Sessions</div>
      <div class="ae-head-right">
        <a href="#" class="ae-link ae-btn-tiny" @click.prevent="$emit('close')">Close</a>
      </div>
    </div>
    <div class="ae-fs-search">
      <input v-model="query" type="search" class="ae-fs-input" placeholder="Search sessions" aria-label="Search sessions" />
    </div>
    <div class="ae-drawer-list">
      <p v-if="loading" class="ae-muted" style="padding: 12px 16px">Loading…</p>
      <p v-else-if="error" class="ae-muted" style="padding: 12px 16px">{{ error }}</p>
      <template v-else-if="query.trim()">
        <button
          v-for="hit in hits" :key="hit.folder + hit.session_id"
          type="button" class="ae-history-row" :class="{ 'is-open': hit.session_id === currentSessionId }"
          @click="$emit('open', hit.session_id)"
        >
          <span class="ae-history-title sensitive" data-spoof-kind="label">{{ hit.title || '(no message)' }}</span>
          <span class="ae-history-meta sensitive" data-spoof-kind="label">{{ hit.where }} · {{ when(hit.created_at) }}</span>
        </button>
        <p v-if="!hits.length" class="ae-muted" style="padding: 12px 16px">No sessions match.</p>
      </template>
      <template v-else>
        <div v-for="folder in tree" :key="folder.key">
          <button type="button" class="ae-fs-folder" @click="toggle(folder.key)">
            <font-awesome-icon :icon="open[folder.key] ? icons.down : icons.right" class="ae-fs-chevron" />
            <font-awesome-icon :icon="open[folder.key] ? icons.folderOpen : icons.folder" class="ae-fs-icon" />
            <span class="ae-fs-label">{{ folder.label }}</span>
            <span class="ae-fs-count">{{ folder.count }}</span>
          </button>
          <template v-if="open[folder.key]">
            <p v-if="!folder.count" class="ae-muted ae-fs-empty">{{ folder.empty }}</p>
            <div v-for="group in folder.groups || []" :key="group.key">
              <button type="button" class="ae-fs-folder ae-fs-depth-1" @click="toggle(group.key)">
                <font-awesome-icon :icon="open[group.key] ? icons.down : icons.right" class="ae-fs-chevron" />
                <font-awesome-icon :icon="open[group.key] ? icons.folderOpen : icons.folder" class="ae-fs-icon" />
                <span class="ae-fs-label sensitive" data-spoof-kind="label">{{ group.label }}</span>
                <span class="ae-fs-count">{{ group.sessions.length }}</span>
              </button>
              <template v-if="open[group.key]">
                <button
                  v-for="ps in group.sessions" :key="ps.session_id"
                  type="button" class="ae-history-row ae-fs-depth-2" :class="{ 'is-open': ps.session_id === currentSessionId }"
                  @click="$emit('open', ps.session_id)"
                >
                  <span class="ae-history-title sensitive" data-spoof-kind="label">{{ ps.title || '(no message)' }}</span>
                  <span v-if="ps.description" class="ae-history-desc sensitive" data-demo-blur>{{ ps.description }}</span>
                  <span class="ae-history-meta">{{ when(ps.created_at) }}</span>
                </button>
              </template>
            </div>
            <button
              v-for="ps in folder.sessions || []" :key="ps.session_id"
              type="button" class="ae-history-row ae-fs-depth-1" :class="{ 'is-open': ps.session_id === currentSessionId }"
              @click="$emit('open', ps.session_id)"
            >
              <span class="ae-history-title sensitive" data-spoof-kind="label">{{ ps.title || '(no message)' }}</span>
              <span v-if="ps.description" class="ae-history-desc sensitive" data-demo-blur>{{ ps.description }}</span>
              <span class="ae-history-meta">
                {{ when(ps.created_at) }}
                <font-awesome-icon v-if="ps.shared" :icon="icons.shared" class="ae-fs-shared" title="Shared with your organisation" />
              </span>
            </button>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronDown, faChevronRight, faFolder, faFolderOpen, faShareNodes } from '@fortawesome/free-solid-svg-icons';

const LIST = '/api/impact_analytics/agent-sessions/';

export default {
  name: 'AgentSessionBrowser',
  components: { FontAwesomeIcon },
  props: {
    currentSessionId: { type: String, default: '' },
    currentTitle: { type: String, default: '' },
    currentDescription: { type: String, default: '' },
  },
  emits: ['open', 'close'],
  data() {
    return {
      icons: { down: faChevronDown, right: faChevronRight, folder: faFolder, folderOpen: faFolderOpen, shared: faShareNodes },
      history: [],
      shared: [],
      loading: true,
      error: '',
      query: '',
      open: { history: true, shared: true },
    };
  },
  computed: {
    mine() {
      return this.history.map((ps) => (ps.session_id === this.currentSessionId
        ? { ...ps, title: this.currentTitle || ps.title, description: this.currentDescription || ps.description }
        : ps));
    },
    sharedRows() {
      return this.shared;
    },
    groups() {
      const byOwner = new Map();
      this.sharedRows.forEach((ps) => {
        const key = ps.is_mine ? 'shared/you' : `shared/${ps.user_name || '?'}`;
        if (!byOwner.has(key)) byOwner.set(key, { key, label: ps.is_mine ? 'You' : (ps.user_name || 'Unknown'), mine: ps.is_mine, sessions: [] });
        byOwner.get(key).sessions.push(ps);
      });
      return [...byOwner.values()].sort((a, b) => (b.mine - a.mine) || a.label.localeCompare(b.label));
    },
    tree() {
      return [
        { key: 'history', label: 'History', count: this.mine.length, sessions: this.mine, empty: 'No sessions yet.' },
        { key: 'shared', label: 'Shared', count: this.sharedRows.length, groups: this.groups, empty: 'Nobody has shared a session yet.' },
      ];
    },
    hits() {
      const needle = this.query.trim().toLowerCase();
      const match = (ps) => `${ps.title || ''} ${ps.description || ''}`.toLowerCase().includes(needle);
      const found = this.mine.filter(match).map((ps) => ({ ...ps, folder: 'history', where: 'History' }));
      this.sharedRows.filter(match).forEach((ps) => {
        found.push({ ...ps, folder: 'shared', where: `Shared / ${ps.is_mine ? 'You' : (ps.user_name || 'Unknown')}` });
      });
      return found.slice(0, 100);
    },
  },
  async mounted() {
    try {
      const [mine, shared] = await Promise.all([
        axios.get(LIST, { params: { folder: 'history' } }),
        axios.get(LIST, { params: { folder: 'shared' } }),
      ]);
      this.history = this.rows(mine.data);
      this.shared = this.rows(shared.data);
    } catch (err) {
      this.error = (err && err.response && err.response.data && err.response.data.error) || 'Could not load sessions.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    rows(data) {
      return Array.isArray(data) ? data : (data.results || []);
    },
    toggle(key) {
      this.open = { ...this.open, [key]: !this.open[key] };
    },
    when(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
